import React, { useEffect, useState, useRef } from "react";
import styles from "./CartCheckoutPage.module.css";
import WebService from "../../Services/WebService";
import { toast } from "react-toastify";
import { FaTrashAlt, FaCheckCircle, FaEdit } from "react-icons/fa";
import { DoorDetail } from "../../Models/Models";
import ChooseVariantModal from "../Watchlist/ChooseVariantModal";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import EditAddressPanel, { EditAddressPanelProps } from "../../Accounts/EditAddressPanel";
import { primaryProductImage } from "../../Services/productImages";
import { useCart } from "../../Contexts/CartContext";
import { cartLineDelivery, cartLinePackaging, cartLineProductTotal } from "../../Services/linePricing";

interface CartItem {
  cart_item_id: number;
  product_id: number;
  product_type: string;
  quantity: number;
  height: number;
  product_name: string;
  product_description: string;
  width: number;
  height_margin: number;
  width_margin: number;
  color: string;
  is_back_design: boolean;
  door_config: boolean;
  wood_quality_id: string;
  packaging_type_id: number;
  description: string;
  mica_id?:  number;
  unit_price?: number;
  packaging_per_unit?: number;
  delivery_per_unit?: number;
  // Backend-computed line amounts — the source of truth for money (see Services/linePricing).
  base_amount?: number | null;
  gst_amount?: number | null;
  packaging_amount?: number | null;
  delivery_amount?: number | null;
  total_price?: number | null;
  product?: DoorDetail;
  [key: string]: any;
}

interface PaymentSummary {
  subtotal: number;
  gst: number;
  delivery: number;
  packaging: number;
  total: number;
}

interface Address {
  address_id: number;
  address_type: string;
  address: string;
  landmark: string;
  city: string | number;
  state: string | number;
  country: string | number;
  complete_address?: string;
  user_id: number;
}

type PaymentMethod = "bank" | "qr" | "cash" | "credit" | "";

interface OptionType {
  id: number;
  name: string;
}

const extractAddresses = (data: any): Address[] => {
  if (!data) return [];
  if (Array.isArray(data.addresses)) return data.addresses;
  if (Array.isArray(data.address)) return data.address;
  return [];
};

const HARDCODED_BANK_DETAILS = {
  holder: "KUNAL ENTERPRISES",
  bank: "HDFC bank",
  acc: "50200030559948",
  ifsc: "HDFC0004199",
  addr: "HDFC BANK PALDA INDORE",
};
const HARDCODED_QR = "/Assets/qr-code.png";



const CartCheckoutPage: React.FC = () => {
  // CUSTOMER BASIC DETAILS (new)
  const [customer, setCustomer] = useState<any>(null);
  const [, setCustomerLoading] = useState(true);
  const { setCartItemCount, refreshCartCount } = useCart();

  // Variant modal state
  const [showVariantModal, setShowVariantModal] = useState(false);
  const [variantItem, setVariantItem] = useState<CartItem | null>(null);

  // CART
  const [cart, setCart] = useState<CartItem[]>([]);
  const [summary, setSummary] = useState<PaymentSummary>({
    subtotal: 0,
    gst: 0,
    delivery: 0,
    packaging: 0,
    total: 0,
  });
  const [cartLoading, setCartLoading] = useState(true);

  const navigate = useNavigate();
  const syncCartBadge = (items: CartItem[]) => {
    const count = items.reduce((sum, item) => sum + (Number(item.quantity) || 0), 0);
    setCartItemCount(count);
  };

  // ADDRESS
  const [addresses, setAddresses] = useState<Address[]>([]);
  const [selectedAddress, setSelectedAddress] = useState<number | null>(null);
  const [addressLoading, setAddressLoading] = useState(true);
  const [showEditPanel, setShowEditPanel] = useState(false);
  const [editInitial, setEditInitial] = useState<EditAddressPanelProps["initial"] | null>(null);
  const [sameAsShipping, setSameAsShipping] = useState<boolean>(true);
 const [isEditingBilling, setIsEditingBilling] = useState<boolean>(false);
 const [billingSaved, setBillingSaved] = useState<boolean>(false);
 const [billingSubmitAttempt, setBillingSubmitAttempt] = useState<boolean>(false);
 const [billingForm, setBillingForm] = useState({
  name: "",
  mobile_number: "",
  country: "",
  state: "",
  city: "",
  address: "",
  landmark: "",
});
//   address_id: number;
//   user_id: number | "";
//   address: string;
//   landmark: string;
//   country: number | "";
//   state: number | "";
//   city: number | "";
// } | null>(null); 

  // DROPDOWN options
  const [countries, setCountries] = useState<OptionType[]>([]);
  const [states, setStates] = useState<OptionType[]>([]);
  const [cities, setCities] = useState<OptionType[]>([]);

  const formatSizeIn = (h: unknown, w: unknown): string | null => {
    const height = Number(h);
    const width = Number(w);
    if (!Number.isFinite(height) || !Number.isFinite(width) || height <= 0 || width <= 0) return null;
    return `${height} x ${width} in`;
  };

  /** Customer-entered size — never replace with bill size. */
  const getOrderSizeLabel = (it: CartItem): string | null => {
    if (typeof it.order_size === "string" && it.order_size.trim()) return it.order_size.trim();
    return formatSizeIn(it.order_height ?? it.height, it.order_width ?? it.width);
  };

  /** Calculated size used for pricing. */
  const getBillSizeLabel = (it: CartItem): string | null => {
    if (typeof it.bill_size === "string" && it.bill_size.trim()) return it.bill_size.trim();
    return formatSizeIn(it.bill_height, it.bill_width);
  };
const mobileValid = (val: string) => /^\d{10,15}$/.test(val.trim());
const fieldError = (val: string) => !val || !val.trim();
const getNameById = (arr: OptionType[], id: string | number) => {
  const s = String(id ?? "");
  return arr.find(x => String(x.id) === s)?.name || "";
};

const canConfirm =
  !!selectedAddress &&
  cart.length > 0 &&
  (sameAsShipping || billingSaved);


  // Notes section
  const [notes, setNotes] = useState("");
  const [hasConfirmed, setHasConfirmed] = useState(false);
  const [saleOrderId, setSaleOrderId] = useState<string | null>(null);
  
  const [cashAmount, setCashAmount] = useState("");
  // Payment & Checkout
  const [selectedPay, setSelectedPay] = useState<PaymentMethod>("");
  const [utr, setUTR] = useState("");
  const [paymentFile, setPaymentFile] = useState<File | null>(null);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
      
   const invalidateConfirmation = React.useCallback(() => {
        setHasConfirmed(false);
        setSaleOrderId(null);
        setSelectedPay("");
        setCashAmount("");
        setUTR("");
        setPaymentFile(null);
      }, []);

  const touchBillingForm = (patch: Partial<typeof billingForm>) => {
    setBillingForm((f) => ({ ...f, ...patch }));
    setBillingSaved(false);
    invalidateConfirmation();
  };

  // 1. Fetch CUSTOMER BASIC DETAILS
  useEffect(() => {
    setCustomerLoading(true);
    WebService.getAPI<any>({ action: "/v1/door-management/customers/basic-details" })
      .then((res) => {
        const data = res?.data || null;
        setCustomer(data);
        const addrs = extractAddresses(data);
        setAddresses(addrs);
        if (addrs.length) setSelectedAddress(Number(addrs[0].address_id));
        setAddressLoading(false);
      })
      .catch(() => {
        setCustomer(null);
        setAddresses([]);
        setAddressLoading(false);
        toast.error("Could not load customer details.");
      })
      .finally(() => setCustomerLoading(false));
  }, []);
 
  // 2. Fetch cart items
  const fetchCart = () => {
    setCartLoading(true);
    WebService.getAPI<{ data: CartItem[] }>({ action: "/v1/door-management/cart" })
      .then((res) => {
        const cartData: CartItem[] = res.data || [];
        setCart(cartData);
        syncCartBadge(cartData);

          // Prefer backend-computed amounts; helpers only fill gaps for display.
        let subtotal = 0;
        let totalDelivery = 0;
        let totalPackaging = 0;

        for (const it of cartData) {
          subtotal += cartLineProductTotal(it);
          totalPackaging += cartLinePackaging(it);
          totalDelivery += cartLineDelivery(it);
        }

        const discount = 0;

        setSummary({
          subtotal,
          gst: 0,
          delivery: totalDelivery,
          packaging: totalPackaging,
          total: subtotal + totalDelivery + totalPackaging - discount,
        });
      })
      .catch(() => {
        setCart([]);
        syncCartBadge([]);
        setSummary({ subtotal: 0, gst: 0, delivery: 0, packaging: 0, total: 0 });
      })
      .finally(() => setCartLoading(false));
  };

  useEffect(() => {
    fetchCart();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 3. Countries/States/Cities mapping (for Add Address modal)
  const [hasLoadedCountries, setHasLoadedCountries] = useState(false);

useEffect(() => {
  if (!sameAsShipping && !hasLoadedCountries) {
    WebService.getAPI<any>({ action: "/v1/door-management/users/countries" })
      .then((res) => {
        const raw: any[] = Array.isArray(res.data) ? res.data : Array.isArray(res.data?.data) ? res.data.data : [];
        setCountries(raw.map((c: any) => ({
          id: c.id ?? c.country_id ?? 0,
          name: c.name ?? c.country_name ?? "",
        })));
        setHasLoadedCountries(true);
      })
      .catch(() => setCountries([]));
  }
}, [sameAsShipping, hasLoadedCountries]);

useEffect(() => {
  if (!billingForm.country) {
    setStates([]);
    setBillingForm(f => ({ ...f, state: "", city: "" }));
    return;
  }
  WebService.getAPI<any>({ action: `/v1/door-management/users/countries/${billingForm.country}/states` })
    .then((res) => {
      const raw: any[] = Array.isArray(res.data) ? res.data : Array.isArray(res.data?.data) ? res.data.data : [];
      setStates(raw.map((s: any) => ({
        id: s.id ?? s.state_id ?? 0,
        name: s.name ?? s.state_name ?? "",
      })));
      setBillingForm(f => ({ ...f, state: "", city: "" }));
    })
    .catch(() => {
      setStates([]);
      setBillingForm(f => ({ ...f, state: "", city: "" }));
    });
}, [billingForm.country]);

useEffect(() => {
  if (!billingForm.state) {
    setCities([]);
    setBillingForm(f => ({ ...f, city: "" }));
    return;
  }
  WebService.getAPI<any>({ action: `/v1/door-management/users/states/${billingForm.state}/cities` })
    .then((res) => {
      const raw: any[] = Array.isArray(res.data) ? res.data : Array.isArray(res.data?.data) ? res.data.data : [];
      setCities(raw.map((c: any) => ({
        id: c.id ?? c.city_id ?? 0,
        name: c.name ?? c.city_name ?? "",
      })));
      setBillingForm(f => ({ ...f, city: "" }));
    })
    .catch(() => {
      setCities([]);
      setBillingForm(f => ({ ...f, city: "" }));
    });
}, [billingForm.state]);

// useEffect(() => {
//   if (!sameAsShipping && hasConfirmed) {
//     invalidateConfirmation();
//   }
// }, [
//   sameAsShipping,
//   billingForm.name,
//   billingForm.mobile_number,
//   billingForm.country,
//   billingForm.state,
//   billingForm.city,
//   billingForm.address,
//   billingForm.landmark,
//   hasConfirmed,
//   invalidateConfirmation, 
// ]);

  //for images
  const getImageSrc = (item: CartItem) =>
    primaryProductImage(
      item.images ??
        item.image ??
        item.product?.images ??
        (item.product as any)?.image ??
        item
    );
const handleSaveBillingLocal = (e: React.FormEvent) => {
  e.preventDefault();
  setBillingSubmitAttempt(true);

  const hasErrors =
    fieldError(billingForm.name) ||
    !mobileValid(billingForm.mobile_number) ||
    fieldError(billingForm.country) ||
    fieldError(billingForm.state) ||
    fieldError(billingForm.city) ||
    fieldError(billingForm.address) ||
    fieldError(billingForm.landmark);

  if (hasErrors) {
    toast.error("Please complete the Shipping address.");
    return;
  }
  setBillingSaved(true);
  setIsEditingBilling(false);
  toast.success("Shipping address saved for this order only.");
};

  const handleDownloadEstimate = async () => {
  if (!saleOrderId) {
    toast.error("Order not confirmed!");
    return;
  }
  try {
    const blob = await WebService.downloadFileAPI({
      action: `/v1/door-management/saleorders/my-saleorders/${saleOrderId}/estimations`,
      method: "GET"
    });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `Estimate_${saleOrderId}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
    toast.success("Estimate downloaded!");
  } catch (e) {
    // toast.error("Failed to download estimate.");
  }
};

const getFullAddress = (a: Address) =>
  (a.complete_address && a.complete_address.trim()) ||
  [a.address, a.landmark && `Near ${a.landmark}`].filter(Boolean).join(", ");

  // For Quantity Update, handle Raw Material and Product fields
const handleUpdateQuantity = async (item: CartItem, newQuantity: number) => {
  if (newQuantity < 1) return;
  try {
    
    let patchPayload: any = {
      cart_item_id: item.cart_item_id,        
      product_id: item.product_id,          
      product_type: item.product_type,        
      quantity: newQuantity,
      height: item.height,
      width: item.width,
      height_margin: item.height_margin,
      width_margin: item.width_margin,
      mica_id: item.mica_id,               
      color: item.color,
      door_config: item.door_config,
      is_back_design: item.is_back_design,
      is_sagwan_bidding: item.is_sagwan_bidding,
      is_back_puc: item.is_back_puc,
      wood_quality_id: item.wood_quality_id,
      packaging_type_id: item.packaging_type_id,
      description: item.description,
     product_name:
    item.product_name ||
    item.product?.product_name ||
    item.product?.name ||
    item.name ||
    "",
  product_description:
    ("design_description" in (item.product || {}) && (item.product as any).design_description) ||
    item.product_description ||
    item.product?.product_description ||
    item.product?.description ||
    item.description ||
    "",
    };


    await WebService.patchAPI({
      action: "/v1/door-management/cart",
      body: patchPayload,
    });
    invalidateConfirmation();  
    fetchCart();
  } catch (e) {
    // toast.error("Could not update quantity.");
  }
};
  // Delete Cart Item
  const handleDeleteCartItem = async (cart_item_id: number) => {
    const confirm = await Swal.fire({
      title: "Remove item?",
      text: "Are you sure you want to remove this item from cart?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#FFD400",
      cancelButtonColor: "#aaa",
      confirmButtonText: "Remove",
      cancelButtonText: "Cancel",
      heightAuto: false,
      focusCancel: true,
    });
    if (!confirm.isConfirmed) return;
    try {
      await WebService.deleteAPI({ action: `/v1/door-management/cart/${cart_item_id}` });
      invalidateConfirmation();
      fetchCart();
      toast.success("Removed from cart.");
    } catch (e) {
      // toast.error("Failed to remove item.");
    }
  };

  const handleEditCartItem = (cart_item_id: number) => {
    const item = cart.find(i => i.cart_item_id === cart_item_id);
    if (!item) return;
    setVariantItem(item);
    setShowVariantModal(true);
  };

const handleEditAddress = (address_id: number) => {
  const addr = addresses.find(a => a.address_id === address_id);
  if (!addr) {
    toast.error("Address not found.");
    return;
  }

  const userId = (addr as any).user_id ?? (customer && customer.user_id);
  if (typeof userId !== "number") {
    toast.error("User id missing.");
    return;
  }

  setEditInitial({
    address_id: addr.address_id,
    user_id: userId, 
    address: addr.address || "",
    landmark: addr.landmark || "",
    country:
      typeof addr.country === "number"
        ? addr.country
        : Number(addr.country) || "",
    state:
      typeof addr.state === "number"
        ? addr.state
        : Number(addr.state) || "",
    city:
      typeof addr.city === "number"
        ? addr.city
        : Number(addr.city) || "",
  });

  setShowEditPanel(true);
};

  // Payment file upload handler
  const fileInput = useRef<HTMLInputElement | null>(null);

  // Confirm before payment
const handleConfirmCheckout = async () => {
  if (!selectedAddress) return toast.error("Please select a shipping address!");

  try {
    // Always send shipping as `address_id`
    const payload: any = {
      address_id: Number(selectedAddress),
      notes,
    };

    // Only add billing_address if different from shipping
    if (!sameAsShipping) {
      setBillingSubmitAttempt(true);

      const invalid =
        fieldError(billingForm.name) ||
        !mobileValid(billingForm.mobile_number) ||
        fieldError(billingForm.country) ||
        fieldError(billingForm.state) ||
        fieldError(billingForm.city) ||
        fieldError(billingForm.address) ||
        fieldError(billingForm.landmark);

      if (invalid) {
        toast.error("Please complete the billing address.");
        return;
      }

      payload.shipping_address = {
        name: billingForm.name.trim(),
        mobile_number: billingForm.mobile_number.trim(),
        address: billingForm.address.trim(),
        landmark: billingForm.landmark.trim(),
        country_id: Number(billingForm.country),
        state_id: Number(billingForm.state),
        city_id: Number(billingForm.city),
      };
    }

    // (Optional) debug:
    // console.log("checkout payload >", payload);

    const res = await WebService.postAPI({
      action: "/v1/door-management/cart/checkout",
      body: payload,
    });

    const orderId =
      (res as any)?.sale_order_id ??
      (res as any)?.data?.sale_order_id ??
      (res as any)?.id ??
      null;

    setSaleOrderId(orderId ? String(orderId) : null);
    setHasConfirmed(true);
    // Backend clears cart on checkout — keep totals for payment UI
    setCart([]);
    syncCartBadge([]);
    toast.success("Order confirmed, Now Select Payment Method!");
  } catch (e: any) {
    toast.error(typeof e === "string" ? e : e?.message || "Could Not Confirm Order.");
  }
};
  // Final payment/checkout with all method support
const handlePaymentCheckout = async () => {
  // Guard: prevent double submit
  if (checkoutLoading) return;

  if (!saleOrderId) {
    toast.error("Order not confirmed. Please confirm first.");
    return;
  }
  if (!selectedPay) {
    toast.error("Please select payment method!");
    return;
  }

  // --- Resolve amount safely ---
  const total = Number(summary?.total ?? 0);
  if (!Number.isFinite(total) || total < 0) {
    toast.error("Invalid total amount.");
    return;
  }

  let payAmount = total;

  // --- CASH LOGIC & validations ---
  if (selectedPay === "cash") {
    const parsed = Number(cashAmount);
    if (!cashAmount || !Number.isFinite(parsed) || parsed <= 0) {
      toast.error("Please enter amount paid in cash!");
      return;
    }
    if (parsed > total) {
      toast.error(`Cash amount cannot be more than total (₹${total.toFixed(2)})`);
      return;
    }
    if (parsed < total) {
      const remaining = total - parsed;
      const swalRes = await Swal.fire({
        title: "Partial Payment",
        html:
          `You are paying only <b>₹${parsed.toFixed(2)}</b>.<br/>` +
          `<span style="color:#c23c3c">₹${remaining.toFixed(2)} is still remaining.</span><br/><br/>Proceed with partial payment?`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#FFD400",
        cancelButtonColor: "#aaa",
        confirmButtonText: "Yes, continue",
        cancelButtonText: "Cancel",
        heightAuto: false,
        focusCancel: true,
      });
      if (!swalRes.isConfirmed) return;
    }
    payAmount = Number(cashAmount);
  }

  // --- BANK / QR validations ---
  if (selectedPay === "bank" && !utr) {
    toast.error("Please enter UTR number!");
    return;
  }
  const needsFile = selectedPay === "bank" || selectedPay === "qr";
  if (needsFile && !paymentFile) {
    toast.error("Please upload a payment screenshot!");
    return;
  }

  // --- Map payment method & transaction id (contract: form-data fields) ---
  const payment_method =
    selectedPay === "bank"
      ? "BANK_TRANSFER"
      : selectedPay === "qr"
      ? "UPI"
      : selectedPay === "cash"
      ? "CASH"
      : selectedPay === "credit"
      ? "CREDIT"
      : "";

  const transaction_id =
    selectedPay === "bank"
      ? String(utr).trim()
      : selectedPay === "qr"
      ? "QR_PAYMENT"
      : selectedPay === "credit"
      ? "CREDIT_PAYMENT"
      : "CASH_PAYMENT";

  // Defensive: ensure strings for form-data
  const payload: Record<string, string> = {
    payment_method,
    transaction_id,
    amount: payAmount.toFixed(2),
    sale_order_id: String(saleOrderId ?? "").trim(),
    // backend in your screenshot doesn't show `notes`, but harmless if accepted; drop if server rejects
    ...(notes ? { notes: String(notes) } : {}),
  };

  setCheckoutLoading(true);
  try {
    // Single path: ALWAYS send multipart/form-data
    const res: any = await WebService.fileUploadAPI({
      action: `/v1/door-management/cart/checkout/confirm`,
      body: payload,
      key: "file_name", 
      file: needsFile ? paymentFile : null, 
    });

    const receiptNo = res?.data?.receipt_number;
    const placedOnCredit = res?.data?.placed_on_credit;
    toast.success(
      placedOnCredit
        ? "Order placed on credit."
        : receiptNo
        ? `Payment uploaded. Receipt ${receiptNo} issued.`
        : "Payment uploaded successfully!"
    );
    // Order is placed — re-read the cart from the server so the page and the
    // navbar badge reflect what actually remains instead of an optimistic guess.
    setCart([]);
    await refreshCartCount();
    setTimeout(() => {
      if (saleOrderId) {
        navigate(`/mysaleorder-management/${saleOrderId}`);
      } else {
        navigate("/mysaleorder-management");
      }
    }, 800);
  } catch (e: any) {
    // Specific credit hint preserved; otherwise generic
    const msg =
      typeof e === "string"
        ? e
        : e?.message ||
          (selectedPay === "credit"
            ? "Credit payment failed. Please check your credit limit."
            : "Payment failed.");
    toast.error(msg);
  } finally {
    setCheckoutLoading(false);
  }
};

  const handleVariantModalClose = () => {
    invalidateConfirmation(); 
    setShowVariantModal(false);
    setVariantItem(null);
    fetchCart();
  };
  const BillingPreview: React.FC = () => {
  const country = getNameById(countries, billingForm.country);
  const state = getNameById(states, billingForm.state);
  const city = getNameById(cities, billingForm.city);

  return (
    <div
      style={{
        marginTop: 10,
        border: "1px solid #eaeaea",
        borderRadius: 8,
        padding: "12px 14px",
        background: "#fafafa",
        display: "grid",
        gap: 6,
      }}
      aria-label="Shipping Address Preview"
    >
      <div style={{ fontWeight: 700, marginBottom: 4 }}>Shipping Address {billingSaved ? <span style={{ color:"#2b6a1b" }}>• Saved</span> : null}</div>
      <div>Consignee Name - {billingForm.name || "—"}</div>
      <div>Mobile No - {billingForm.mobile_number || "—"}</div>
      <div>
        Address - {(billingForm.address || "—")}{billingForm.landmark ? `, ${billingForm.landmark}` : ""}
        { [city, state, country].filter(Boolean).length ? `, ${[city, state, country].filter(Boolean).join(", ")}` : "" }
      </div>
      <div style={{ marginTop: 6 }}>
        <button
          className={styles.saveAddrBtn}
          onClick={() => setIsEditingBilling(true)}
          type="button"
        >
          Edit
        </button>
      </div>
    </div>
  );
};

  // Mirrors the backend: customer's saved discount % (dealer/broker) on the items subtotal only.
  const discountPer = Math.min(Math.max(Number(customer?.default_discount_per) || 0, 0), 100);
  const discountAmount = Math.round(summary.subtotal * discountPer) / 100;
  const payableTotal = Math.max(Math.round((summary.total - discountAmount) * 100) / 100, 0);

  return (
    <div className={styles.checkoutPage}>
      {/* CART SECTION */}
      <div className={styles.cartSection}>
        <h2>My Cart</h2>
        <div className={styles.cartItemList}>
          {cartLoading ? (
            <div className={styles.loading}>Loading...</div>
          ) : cart.length === 0 && hasConfirmed ? (
            <div className={styles.emptyCart}>
              <div
                style={{
                  fontSize: 18,
                  fontWeight: 600,
                  color: "#2e7d32",
                  textAlign: "center",
                  marginTop: 24,
                }}
              >
                Order confirmed. Choose a payment method below.
              </div>
            </div>
          ) : cart.length === 0 ? (
<div className={styles.emptyCart}>
  <img
    src="/Assets/empty-cart.svg"
    alt="Your cart is empty"
    style={{
      width: 240,
      margin: "32px auto 12px auto",
      display: "block",
      opacity: 0.7,
    }}
  />

  <div
    style={{
      fontSize: 20,
      fontWeight: 600,
      color: "#b0b0b0",
      textAlign: "center",
    }}
  >
    Your cart is empty
  </div>

  <div style={{ textAlign: "center", marginTop: 8, color: "#b7b7b7" }}>
    Continue shopping to add products.
  </div>

  <button
    type="button"
    onClick={() => navigate("/shop")}
    style={{
      marginTop: 14,
      display: "block",
      marginLeft: "auto",
      marginRight: "auto",
      background: "#ffd600",
      border: "none",
      color: "#222",
      fontWeight: 700,
      padding: "10px 16px",
      borderRadius: 8,
      cursor: "pointer",
    }}
    title="Go to Shop"
  >
    Continue shopping →
  </button>
</div>
          ) : (
            cart.map((item, idx) => {
              const qty = item.quantity || 0;
              const productTotal = cartLineProductTotal(item);
              const baseTotal =
                typeof item.base_amount === "number" && Number.isFinite(item.base_amount)
                  ? item.base_amount
                  : productTotal;
              const deliveryTotal = cartLineDelivery(item);
              const packagingTotal = cartLinePackaging(item);
              const sqft = typeof item.sqft === "number" ? item.sqft : null;
              const isRaw = (item.product_type || "").toUpperCase() === "RAW_MATERIAL";
              return (
                <div className={styles.cartItem} key={item.cart_item_id}>
                  <div className={styles.srBadge} aria-label={`Item serial ${idx + 1}`}>{idx + 1}</div>
                  <img
                    className={styles.productImg}
                    src={getImageSrc(item)}
                    alt={item.product?.name || item.product?.product_name || item.product_name || item.name || "Product"}
                    onError={(e) => {
                      (e.currentTarget as HTMLImageElement).src = "/Assets/no-pictures.png";
                      (e.currentTarget as HTMLImageElement).onerror = null;
                    }}
                  />
                  <div className={styles.cartItemInfo}>
                    <div className={styles.itemTitle}>
                      {item.product?.name || item.product_name || item.name || "Product"}
                    </div>
                      {(() => {
                        const orderSize = getOrderSizeLabel(item);
                        const billSize = getBillSizeLabel(item);
                        return (
                          <>
                            {orderSize ? (
                              <div className={styles.itemMeta}>Order Size: {orderSize}</div>
                            ) : null}
                            {billSize ? (
                              <div className={styles.itemMeta}>Bill Size: {billSize}</div>
                            ) : null}
                          </>
                        );
                      })()}
                      {isRaw && sqft != null && sqft > 0 && (
                        <div className={styles.itemMeta}>
                          {sqft.toFixed(2)} sqft × {qty} × ₹{Number(item.unit_price ?? 0).toLocaleString()}/sqft
                        </div>
                      )}
                      {!isRaw && sqft != null && sqft > 0 && (
                        <div className={styles.itemMeta}>
                          {sqft.toFixed(2)} sqft × {qty} × ₹{Number(item.unit_price ?? 0).toLocaleString()}/sqft
                        </div>
                      )}
                    <div className={styles.itemBreakdown}>
                      <div>Product: <b>₹{Number(baseTotal).toLocaleString()}</b></div>
                      <div>Packaging: <b>₹{packagingTotal.toLocaleString()}</b></div>
                      <div>Delivery: <b>₹{deliveryTotal.toLocaleString()}</b></div>
                    </div>
                  </div>
                  <div className={styles.cartItemActions}>
                    <div className={styles.price}>₹{productTotal.toLocaleString()}</div>
                    <button
                      className={styles.qtyBtn}
                      onClick={() => handleUpdateQuantity(item, item.quantity - 1)}
                      disabled={item.quantity <= 1}
                      style={{ margin: "0 7px" }}
                    >–</button>
                    <span className={styles.qtyValue}>{item.quantity}</span>
                    <button
                      className={styles.qtyBtn}
                      onClick={() => handleUpdateQuantity(item, item.quantity + 1)}
                      style={{ margin: "0 7px" }}
                    >+</button>
                    <FaEdit
                      className={styles.editIcon}
                      style={{ marginRight: 7, cursor: "pointer" }}
                      onClick={() => handleEditCartItem(item.cart_item_id)}
                      title="Edit"
                    />
                    <FaTrashAlt
                      className={styles.trashIcon}
                      onClick={() => handleDeleteCartItem(item.cart_item_id)}
                      title="Delete"
                      style={{ marginLeft: 4 }}
                    />
                  </div>
                </div>
              );
            })
          )}
        </div>
        {/* SUMMARY */}
        {(cart.length > 0 || hasConfirmed) && (
          <div className={styles.paymentSummary}>
            <div className={styles.summaryRow}>
              <span>Sub Total Product</span>
              <span>₹{summary.subtotal.toLocaleString()}</span>
            </div>
            {summary.gst > 0 && (
              <div className={styles.summaryRow}>
                <span>GST (included in product)</span>
                <span>₹{summary.gst.toLocaleString()}</span>
              </div>
            )}
            <div className={styles.summaryRow}>
              <span>Total Delivery</span>
              <span>₹{summary.delivery.toLocaleString()}</span>
            </div>
            <div className={styles.summaryRow}>
              <span>Total Packaging</span>
              <span>₹{summary.packaging.toLocaleString()}</span>
            </div>
            {discountAmount > 0 && (
              <div className={styles.summaryRow}>
                <span>Discount ({discountPer}%)</span>
                <span>-₹{discountAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
              </div>
            )}
            <hr />
            <div className={styles.summaryRow} style={{ fontWeight: 600 }}>
              <span>Total</span>
              <span>
              ₹{Number(payableTotal).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
               </span>

            </div>
          </div>
        )}
      </div>
<div className={styles.addressSection}>
  <div className={styles.addressHeader}>
    <FaCheckCircle style={{ color: "#f1e317ff", marginRight: 10 }} />
    <span>Billing Address</span>

  </div>

  {addressLoading ? (
    <div className={styles.loading}>Loading...</div>
  ) : (
    <div className={styles.addressList}>
      {addresses.length === 0 ? (
        <div className={styles.loading} style={{ marginBottom: 12 }}>
          No shipping address yet. Add one below to enable Confirm.
        </div>
      ) : null}
      {addresses.map((addr) => (
        <div className={styles.addrCard} key={addr.address_id}>
          <input
            type="radio"
            checked={Number(selectedAddress) === Number(addr.address_id)}
            onChange={() => {
              setSelectedAddress(Number(addr.address_id));
              if (hasConfirmed) invalidateConfirmation();
            }}
          />
          <div>
            <div>{getFullAddress(addr)}</div>
          </div>

          <FaEdit
            className={styles.editIcon}
            style={{ marginLeft: "auto", cursor: "pointer" }}
            onClick={() => handleEditAddress(addr.address_id)}
            title="Edit"
          />
        </div>
      ))}
    </div>
  )}

  {/* ✅ New: Billing Same-as-Shipping checkbox */}
  <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "10px 0 6px 0" }}>
    <label style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 600 }}>
      <input
        type="checkbox"
        checked={sameAsShipping}
        onChange={(e) => {
          const checked = e.target.checked;
          setSameAsShipping(checked);
          setBillingSaved(false);
          setBillingSubmitAttempt(false);
          setIsEditingBilling(!checked);
          if (hasConfirmed) invalidateConfirmation(); 
        }}
      />
    Shipping Address same as Blling Address
    </label>
  </div>

  {/* If different -> show Billing editor or its preview */}
  {!sameAsShipping && (
    <>
      {isEditingBilling ? (
<form
  className={styles.addAddrForm}
  onSubmit={handleSaveBillingLocal}
  style={{ marginTop: 6 }}
>
  <h4>Shipping Address</h4>

  <input
    placeholder="Consignee Name"
    value={billingForm.name}
    onChange={(e) => touchBillingForm({ name: e.target.value })}
    className={billingSubmitAttempt && fieldError(billingForm.name) ? styles.invalid : undefined}
  />
  {billingSubmitAttempt && fieldError(billingForm.name) && (
    <div className={styles.errorMsg}>Name is required</div>
  )}

  <input
    placeholder="Mobile Number"
    value={billingForm.mobile_number}
    onChange={(e) => touchBillingForm({ mobile_number: e.target.value })}
    className={billingSubmitAttempt && !mobileValid(billingForm.mobile_number) ? styles.invalid : undefined}
  />
  {billingSubmitAttempt && !mobileValid(billingForm.mobile_number) && (
    <div className={styles.errorMsg}>Valid mobile is required</div>
  )}

  <select
    value={billingForm.country}
    onChange={(e) => touchBillingForm({ country: e.target.value })}
    className={billingSubmitAttempt && fieldError(billingForm.country) ? styles.invalid : undefined}
  >
    <option value="">Select Country</option>
    {countries.map(c => <option key={c.id || c.name} value={c.id}>{c.name}</option>)}
  </select>
  {billingSubmitAttempt && fieldError(billingForm.country) && (
    <div className={styles.errorMsg}>Country is required</div>
  )}

  <select
    value={billingForm.state}
    onChange={(e) => touchBillingForm({ state: e.target.value })}
    disabled={!billingForm.country}
    className={billingSubmitAttempt && fieldError(billingForm.state) ? styles.invalid : undefined}
  >
    <option value="">Select State</option>
    {states.map(s => <option key={s.id || s.name} value={s.id}>{s.name}</option>)}
  </select>
  {billingSubmitAttempt && fieldError(billingForm.state) && (
    <div className={styles.errorMsg}>State is required</div>
  )}

  <select
    value={billingForm.city}
    onChange={(e) => touchBillingForm({ city: e.target.value })}
    disabled={!billingForm.state}
    className={billingSubmitAttempt && fieldError(billingForm.city) ? styles.invalid : undefined}
  >
    <option value="">Select City</option>
    {cities.map(c => <option key={c.id || c.name} value={c.id}>{c.name}</option>)}
  </select>
  {billingSubmitAttempt && fieldError(billingForm.city) && (
    <div className={styles.errorMsg}>City is required</div>
  )}

  <input
    placeholder="Address"
    value={billingForm.address}
    onChange={(e) => touchBillingForm({ address: e.target.value })}
    className={billingSubmitAttempt && fieldError(billingForm.address) ? styles.invalid : undefined}
  />
  {billingSubmitAttempt && fieldError(billingForm.address) && (
    <div className={styles.errorMsg}>Address is required</div>
  )}

  <input
    placeholder="Landmark"
    value={billingForm.landmark}
    onChange={(e) => touchBillingForm({ landmark: e.target.value })}
    className={billingSubmitAttempt && fieldError(billingForm.landmark) ? styles.invalid : undefined}
  />
  {billingSubmitAttempt && fieldError(billingForm.landmark) && (
    <div className={styles.errorMsg}>Landmark is required</div>
  )}

  <div style={{ display: "flex", gap: 10 }}>
    <button type="submit" className={styles.saveAddrBtn}>
      {billingSaved ? "Saved ✓" : "Save"}
    </button>
    <button
      type="button"
      className={styles.saveAddrBtn}
      style={{ background: "#eee", color: "#333" }}
      onClick={() => { if (billingSaved) setIsEditingBilling(false); }}
    >
      Cancel
    </button>
  </div>
</form>

      ) : (
        <BillingPreview />
      )}
    </>
  )}
</div>

      {/* NOTES & CONFIRM SECTION */}
      <div className={styles.notesSection}>
        <label className={styles.notesLabel}>
          Notes (optional)
        </label>
              <input
                className={styles.notesInput}
                placeholder="Leave a Note for Seller"
                value={notes}
                maxLength={200}
                onChange={e => {
                  setNotes(e.target.value);
                  if (hasConfirmed) invalidateConfirmation();  
                }}
              />
        {!hasConfirmed && (
          <>
            {!sameAsShipping && !billingSaved && (
              <div style={{ color: "#b45309", fontWeight: 600, marginBottom: 8, fontSize: 14 }}>
                Fill and save the separate shipping address before confirming.
              </div>
            )}
            <button
              className={styles.confirmBtn}
              onClick={handleConfirmCheckout}
              disabled={!canConfirm}
              title={
                !selectedAddress
                  ? "Add and select a billing address first"
                  : cart.length === 0
                    ? "Cart is empty"
                    : !sameAsShipping && !billingSaved
                      ? "Save the separate shipping address first"
                      : "Confirm order"
              }
            >
              Confirm
            </button>
          </>
        )}
      </div>
          {hasConfirmed && saleOrderId && (
        <button
          className={styles.downloadEstimateBtn}
          onClick={handleDownloadEstimate}
          style={{ margin: "18px 0" }}
        >
          Download Estimate PDF
        </button>
      )}
    
      {/* PAYMENT SECTION */}
      {hasConfirmed && (
        <div className={styles.paymentRow}>
          {/* LEFT: Method Selection */}
          <div className={styles.paymentMethodBox}>
            <h3>Payment Method</h3>
            <button
              className={`${styles.payOptBtn} ${selectedPay === "bank" ? styles.paySelected : ""}`}
              onClick={() => setSelectedPay("bank")}
            >
              <span role="img" aria-label="bank">🏦</span> Bank Transfer
            </button>
            <button
              className={`${styles.payOptBtn} ${selectedPay === "qr" ? styles.paySelected : ""}`}
              onClick={() => setSelectedPay("qr")}
            >
              <span role="img" aria-label="qr">📱</span> Scan QR Code
            </button>
            <button
              className={`${styles.payOptBtn} ${selectedPay === "cash" ? styles.paySelected : ""}`}
              onClick={() => setSelectedPay("cash")}
            >
              <span role="img" aria-label="cash">💵</span> Cash
            </button>
            {/* CREDIT button only if allowed and limit > 0 */}
            {customer?.is_credit_allowed && customer?.credit_limit > 0 && (
              <button
                className={`${styles.payOptBtn} ${selectedPay === "credit" ? styles.paySelected : ""}`}
                onClick={() => setSelectedPay("credit")}
              >
                <span role="img" aria-label="credit">💳</span> Use Credit
                <span style={{ fontSize: 13, color: "#7b7700", fontWeight: 600, marginLeft: 9 }}>
                  (Initial Credit Limit: ₹{customer.credit_limit?.toLocaleString()})
                </span>
              </button>
            )}
          </div>

          {/* RIGHT: Payment Details */}
          <div className={styles.paymentDetailBox}>
            <h3>Payment Details</h3>
            {selectedPay === "bank" && (
              <div className={styles.bankDetails}>
                <div><b>Bank holder name:</b> {HARDCODED_BANK_DETAILS.holder}</div>
                <div><b>Bank Name:</b> {HARDCODED_BANK_DETAILS.bank}</div>
                <div><b>Account Number:</b> {HARDCODED_BANK_DETAILS.acc}</div>
                <div><b>IFSC Code:</b> {HARDCODED_BANK_DETAILS.ifsc}</div>
                <div><b>Address:</b> {HARDCODED_BANK_DETAILS.addr}</div>
              </div>
            )}
            {selectedPay === "qr" && (
              <div className={styles.qrSection}>
                <img src={HARDCODED_QR} alt="QR Code" className={styles.qrImg} />
                <div>Scan this QR to pay with any UPI App</div>
              </div>
            )}

            {(selectedPay === "bank" || selectedPay === "qr") && (
              <>
                <div className={styles.paymentUpload}>
                  <label>
                    <b>Upload Payment screenshot</b>
                    <input
                      type="file"
                      ref={fileInput}
                      style={{ display: "none" }}
                      accept=".jpg,.jpeg,.png,.pdf"
                      onChange={e => setPaymentFile(e.target.files?.[0] || null)}
                    />
                    <div
                      className={styles.uploadBox}
                      onClick={() => fileInput.current?.click()}
                    >
                      {paymentFile
                        ? paymentFile.name
                        : <span>Click or drag to upload</span>
                      }
                    </div>
                  </label>
                </div>
                {selectedPay === "bank" && (
                  <div className={styles.utrBox}>
                    <label><b>Enter UTR number</b></label>
                    <input
                      className={styles.utrInput}
                      placeholder="UTR Number"
                      value={utr}
                      onChange={e => setUTR(e.target.value)}
                    />
                  </div>
                )}
                <button
                  className={styles.checkoutBtn}
                  onClick={handlePaymentCheckout}
                  disabled={checkoutLoading}
                >
                  {checkoutLoading ? "Processing..." : `Checkout & Pay ₹${payableTotal}`}
                </button> 
              </>
            )}

            {/* Cash: Just confirm and post */}
{selectedPay === "cash" && (
  <>
    <div className={styles.utrBox}>
      <label><b>Enter Amount Paid (Cash)</b></label>
      <input
        className={styles.utrInput}
        type="number"
        min={0}
        max={payableTotal}
        placeholder={`Enter amount (max ₹${payableTotal})`}
        value={cashAmount}
        onChange={e => setCashAmount(e.target.value.replace(/[^0-9.]/g, ""))}
      />
    </div>
    <button
      className={styles.checkoutBtn}
      onClick={handlePaymentCheckout}
      disabled={checkoutLoading}
    >
      {checkoutLoading ? "Processing..." : `Confirm Cash Payment`}
    </button>
  </>
)}


            {/* Credit: Show limit, just confirm and post */}
            {selectedPay === "credit" && (
              <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                <div style={{
                  background: "#fffbe7",
                  border: "1.5px solid #ffe066",
                  borderRadius: 9,
                  padding: "14px 18px",
                  marginBottom: 6,
                  fontWeight: 600,
                  color: "#7b7700",
                }}>
                  Initial Credit Limit: ₹{customer.credit_limit?.toLocaleString()}
                </div>
                <button
                  className={styles.checkoutBtn}
                  onClick={handlePaymentCheckout}
                  disabled={checkoutLoading}
                >
                  {checkoutLoading ? "Processing..." : `Checkout with Credit`}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* VARIANT MODAL */}
{showVariantModal && variantItem && (() => {
  const isRawMaterial = variantItem.product_type === "RAW_MATERIAL";
  const measuringUnit =
    (variantItem.product as any)?.measuring_unit ||
    (variantItem as any)?.measuring_unit ||
    "";

  // Only add height/width for RAW_MATERIAL with "square feet"
  const extraProps =
    isRawMaterial && measuringUnit.trim().toLowerCase() === "square feet"
      ? { height: variantItem.height, width: variantItem.width }
      : {};

  // Prefer cart item's images, otherwise product images, otherwise empty
  const images =
    (Array.isArray((variantItem as any).images) &&
      (variantItem as any).images.length
      ? (variantItem as any).images
      : variantItem.product?.images) ?? [];

  const idGuess =
    (variantItem.product as any)?.id ?? variantItem.product_id ?? 0;

  const nameGuess =
    variantItem.product?.name ??
    variantItem.product?.product_name ??
    variantItem.product_name ??
    "";

  const productNameGuess =
    variantItem.product?.product_name ??
    variantItem.product?.name ??
    variantItem.product_name ??
    "";

  const descriptionGuess =
    (("design_description" in (variantItem.product || {})) &&
      (variantItem.product as any).design_description) ||
    variantItem.product?.product_description ||
    variantItem.product?.description ||
    variantItem.product_description ||
    "";

  const skuGuess =
    (variantItem.product as any)?.sku_id ??
    (variantItem as any)?.sku_id ??
    "";

  // Build a DoorDetail that satisfies the union requirements
  const productForModal: DoorDetail = (isRawMaterial
    ? {
        type: "RAW_MATERIAL",
        id: idGuess,
        sku_id: skuGuess,
        name: nameGuess,
        product_name: productNameGuess,
        description: descriptionGuess,
        images,
        // raw-material specific, if you have them:
        measuring_unit:
          (variantItem.product as any)?.measuring_unit ??
          (variantItem as any)?.measuring_unit ??
          "",
        measuring_unit_value:
          (variantItem.product as any)?.measuring_unit_value,
      }
    : {
        type: "PRODUCT",
        id: idGuess,
        sku_id: skuGuess,
        name: nameGuess,
        product_name: productNameGuess,
        description: descriptionGuess,
        images,
        // product specific (optional extras):
        rating: (variantItem.product as any)?.rating ?? 0,
        review_count: (variantItem.product as any)?.review_count ?? 0,
      }) as DoorDetail;

  return (
    <ChooseVariantModal
      onClose={handleVariantModalClose}
      cartItem={{
        ...variantItem,
        // ✅ pass a DoorDetail
        product: productForModal,

        // Keep these top-level strings the modal uses elsewhere
        product_name: productNameGuess,
        product_description: descriptionGuess,

        ...extraProps,
      }}
      itemType={isRawMaterial ? "RAW_MATERIAL" : "PRODUCT"}
        onUpdated={() => {                
    invalidateConfirmation();
    fetchCart();
  }}

    />
  );
})()}
{/* EDIT ADDRESS PANEL */}
{showEditPanel && editInitial && (
  <EditAddressPanel
    open={showEditPanel}
    initial={editInitial}
    mode="self"
    onClose={() => setShowEditPanel(false)}
    onSaved={() => {
      setShowEditPanel(false);
       invalidateConfirmation(); 
      // refresh addresses (same logic you used after adding a new address)
      setCustomerLoading(true);
      WebService.getAPI<any>({ action: "/v1/door-management/customers/basic-details" })
        .then((res) => {
          const data = res?.data || null;
          setCustomer(data);
          const addrs = extractAddresses(data);
          setAddresses(addrs);
          if (addrs.length) setSelectedAddress(Number(addrs[0].address_id));
          setAddressLoading(false);
        })
        .catch(() => {
          setCustomer(null);
          setAddresses([]);
          setAddressLoading(false);
        })
        .finally(() => setCustomerLoading(false));
    }}
  />
)}
    </div>
  );
};
export default CartCheckoutPage;
